/**
 * SessionShellCard — session users card with language switch and reset button.
 * Extracted from App.tsx — pure structural refactor.
 */

import { useState } from "react";

import {
  selectCurrentUser,
  useEditorStore,
} from "../state/editorStore";
import {
  SUPPORTED_LANGUAGES,
  useLanguage,
  useT,
  type LanguageCode,
} from "../i18n";
import { toTestIdSegment } from "../helpers/formatHelpers";
import { PreviewHighlightingDialog } from "./PreviewHighlightingDialog";

export type SessionShellCardProps = {
  onReset: () => void;
  /**
   * Open the Font Manager dialog. Wired up in `App.tsx` — the dialog
   * itself lives at the App root so it can also be opened from the
   * text-style editor's "Manager..." button.
   */
  onOpenFontManager: () => void;
};

export function SessionShellCard({
  onReset,
  onOpenFontManager,
}: SessionShellCardProps) {
  const t = useT();
  const { language, setLanguage } = useLanguage();
  const state = useEditorStore((store) => store.state);
  const dispatch = useEditorStore((store) => store.dispatch);
  const currentUser = selectCurrentUser(state);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  return (
    <article className="surface-card" data-testid="session-shell-card">
      <div className="section-heading">
        <div>
          <p className="section-label">{t("sessions.section_label")}</p>
          <h2>{t("sessions.title")}</h2>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* Language switcher — RU is the default per project
              requirements; the chosen language persists to
              localStorage so it sticks across reloads. */}
          <label
            style={{ fontSize: 11, opacity: 0.7 }}
            htmlFor="i18n-language-select"
          >
            {t("settings.language")}
          </label>
          <select
            id="i18n-language-select"
            data-testid="settings-language-select"
            className="context-select"
            value={language}
            onChange={(e) => setLanguage(e.target.value as LanguageCode)}
          >
            {SUPPORTED_LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {t(`settings.language.${lang}` as never)}
              </option>
            ))}
          </select>
          <button
            className="ghost-button"
            type="button"
            onClick={onOpenFontManager}
            data-testid="session-manage-fonts"
          >
            {t("sessions.manage_fonts")}
          </button>
          <button
            className="ghost-button"
            type="button"
            onClick={() => setPreviewDialogOpen(true)}
            data-testid="session-preview-settings"
          >
            {t("settings.preview.open")}
          </button>
          <button
            className="ghost-button"
            type="button"
            onClick={onReset}
            data-testid="reset-state-button"
          >
            {t("sessions.reset_state")}
          </button>
        </div>
      </div>

      <div className="session-grid" data-testid="session-grid">
        {state.session.users.map((user) => {
          const isCurrent = user.email === currentUser.email;

          return (
            <button
              key={user.email}
              className={`session-card ${isCurrent ? "is-selected" : ""}`}
              type="button"
              data-testid={`session-card-${toTestIdSegment(user.email)}`}
              onClick={() =>
                dispatch({
                  type: "session/switch-user",
                  source: "session",
                  userEmail: user.email,
                })
              }
            >
              <span className="session-name">{user.displayName}</span>
              <span className="session-email">{user.email}</span>
              <span className="session-meta">
                {user.role} / {user.status}
              </span>
            </button>
          );
        })}
      </div>

      <PreviewHighlightingDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
      />
    </article>
  );
}
